import _ from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Checkbox, Loader } from "rsuite";

import {
  checkSignable,
  contractSettings,
  availablPeriodItems,
  getAutoControlLabel,
  getAutoControl2Label,
} from "../../utils/sign";

import Input from "../Input";
import Select from "../Select";
import EquipmentSet from "./EquipmentSet";
import FileService from "../../services/fileService";

const RenewalForm = ({
  prescription,
  prescriber,
  prescribers,
  onSubmit,
  setSignatureLink,
  printMode,
  isSuperAdmin,
}) => {
  const periodItems = availablPeriodItems(prescription.contract.plan.code);
  const [isLoading, setIsLoading] = useState(true);
  const [finessItems, setFinessItems] = useState([]);
  const [currentFinessItem, setCurrentFinessItem] = useState({
    label: "Autre",
    value: "other",
  });
  const [otherFiness, setOtherFiness] = useState(false);
  const [prescriberItems, setPrescriberItems] = useState([]);
  const [prescriberItem, setPrescriberItem] = useState(prescriber);
  const [currentPrescriberItem, setCurrentPrescriberItem] =
    useState(prescriber);
  const [currentPeriodItem, setCurrentPeriodItem] = useState(
    periodItems[0].value
  );
  const [careSetChecked, setCareSetChecked] = useState(true);
  const [tegaderm6X7Checked, setTegaderm6X7Checked] = useState(true);
  const [tegaderm10X15Checked, settegaderm10X15Checked] = useState(true);
  const [missingDataChecked, setMissingDataChecked] = useState(false);
  const [autoControlLabel] = useState(
    getAutoControlLabel(prescription.contract.plan.code)
  );
  const [autoControl2Label] = useState(
    getAutoControl2Label(prescription.contract.plan.code)
  );
  const [settings, setSettings] = useState({
    enabled: false,
    label: "",
    items: {
      careSet: { display: false, default: 1 },
      tegaderm6X7: { display: false, default: 1 },
      tegaderm10X15: { display: false, default: 1 },
      catheter: { display: false, default: "" },
      autocontrol: { display: false, default: false },
      autocontrol2: { display: false, default: false },
    },
  });
  const [adminMode, setAdminMode] = useState("print");

  const fileService = new FileService();

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    getValues,
    formState: { isSubmitting, errors },
  } = useForm();

  useEffect(() => {
    const items = [
      {
        label: `${prescriber.user?.fullName} - ${prescriber.rpps}`,
        value: prescriber._id,
      },
      ..._.map(_.reject(prescribers, { _id: prescriber._id }), (p) => ({
        label: `${p.user?.fullName} - ${p.rpps}`,
        value: p._id,
      })),
    ];
    setPrescriberItems(items);
  }, [prescribers, prescriber]);

  useEffect(() => {
    setSettings(contractSettings(prescription.contract.plan.code));
    setIsLoading(false);
  }, [prescription.contract.plan.code, setValue, getValues]);

  const formatFinessItems = (finess) => {
    const items = [];
    finess.map((f, index) => {
      items.push({
        label: `${f.finnesNumber} - ${f.name}`,
        value: String(index),
      });
      return f;
    });
    return items;
  };

  useEffect(() => {
    const defaultFinessItems =
      prescriber.finess &&
      Array.isArray(prescriber.finess) &&
      prescriber.finess.length > 0
        ? formatFinessItems(prescriber.finess)
        : [];

    defaultFinessItems.push({ label: "Autre", value: "other" });

    setFinessItems(defaultFinessItems);
    setCurrentFinessItem(defaultFinessItems[0]);
    if (defaultFinessItems.length === 1) setOtherFiness(true);

    const defaultValues = {
      startDate: new Date(prescription.startDate),
      period: periodItems[0].value,
      finess:
        defaultFinessItems[0].value !== "other"
          ? defaultFinessItems[0].label
          : null,
      finessOther: "",
      socialReason: "",
      autocontrol: false,
      ...Object.keys(settings.items).reduce((acc, key) => {
        if (settings.items[key].display) {
          acc[key] = settings.items[key].default;
        }
        return acc;
      }, {}),
    };

    reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prescriber.finess, settings]);

  if (!settings.enabled) {
    return (
      <div className="flex justify-center text-primary p-3">
        {isLoading ? (
          <Loader />
        ) : (
          <div>
            Le renouvellement du type{" "}
            <span className="font-bold text-dark">
              {prescription.contract.plan.code}
            </span>{" "}
            n'est pas disponible sur le site pour le moment.
          </div>
        )}
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(async (data) => {
        try {
          console.log({ code: prescription.contract.plan.code, ...data });
          if (
            checkSignable({ code: prescription.contract.plan.code, ...data })
          ) {
            const mode = printMode
              ? "print"
              : isSuperAdmin
              ? adminMode
              : "sign";
            setMissingDataChecked(false);
            if (!data.finess) {
              data.finess = `${data.finessOther} - ${data.socialReason}`;
            }
            const result = await onSubmit(
              prescription._id,
              {
                prescriber: prescriberItem,
                ...data,
              },
              mode
            );

            if (result.signatureLink) {
              setSignatureLink(result.signatureLink);
            } else {
              await fileService.downloadFile(result);
            }
          } else {
            setMissingDataChecked(true);
          }
        } catch (error) {}
      })}
      className="flex flex-col justify-center gap-5 p-3"
    >
      <div className="flex flex-col">
        <span>
          Vous vous apprêtez à renouveler la prescription de{" "}
          <span className="font-semibold">{settings.label}</span> pour le
          patient <span className="font-semibold">{prescription.patient}</span>{" "}
          <span>pour la période suivante :</span>
        </span>
        <div className="flex gap-5 py-3 border-b items-center border-b-gray-300 flex-col md:flex-row">
          <div className="flex gap-2 items-center">
            <label className="text-xs font-semibold text-neutral-dark">
              Date de début<span className="text-red-500">*</span> :
            </label>
            <div>{moment(prescription.startDate).format("DD/MM/YYYY")}</div>
          </div>

          <div className="flex gap-2 items-center">
            <label className="text-xs font-semibold text-neutral-dark">
              Période<span className="text-red-500">*</span> :
            </label>
            <Select
              data={periodItems}
              size="sm"
              value={currentPeriodItem}
              placeholder="Période de prescription"
              onChange={(value) => {
                setCurrentPeriodItem(value);
                setValue("period", value);
              }}
            />
          </div>
        </div>

        {(printMode || isSuperAdmin) &&
          (Array.isArray(prescriberItems) && prescriberItems.length > 1 ? (
            <div className="flex flex-col gap-2 py-3 border-b border-b-gray-300">
              <p>
                Vous allez générer un renouvellement avec le nom du prescripteur
                :
              </p>
              <Select
                data={prescriberItems}
                placeholder="Prescripteur"
                value={currentPrescriberItem}
                onChange={(value) => {
                  const item = _.find(prescribers, { _id: Number(value) });
                  const newFiness = formatFinessItems(item.finess);
                  newFiness.push({ label: "Autre", value: "other" });
                  setPrescriberItem(item);
                  setCurrentPrescriberItem(value);
                  setFinessItems(newFiness);

                  if (newFiness.length > 1) {
                    setValue("finess", newFiness[0].label);
                    setCurrentFinessItem(newFiness[0]);
                    setOtherFiness(false);
                  } else {
                    setValue("finess", null);
                    setCurrentFinessItem({ label: "Autre", value: "other" });
                    setOtherFiness(true);
                  }
                }}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-2 py-3 border-b border-b-gray-300">
              <p>
                Vous allez générer un renouvellement avec le nom du prescripteur{" "}
                <span className="font-semibold">
                  {prescriber?.user?.fullName}
                </span>
              </p>
            </div>
          ))}
        <div className="flex flex-col gap-2 py-3 border-b border-b-gray-300">
          <p>
            Veuillez selectionner le <strong>finess</strong>
            <span className="text-red-500">*</span>
          </p>
          <Select
            data={finessItems}
            placeholder="Finess"
            value={currentFinessItem}
            onChange={(value) => {
              const currentItem = finessItems.find(
                (item) => item.value === value
              );
              if (value !== "other") {
                setValue("finess", currentItem.label);
                setCurrentFinessItem(currentItem);
                setOtherFiness(false);
              } else {
                setValue("finess", null);
                setCurrentFinessItem({ label: "Autre", value: "other" });
                setOtherFiness(true);
              }
            }}
          />
          {otherFiness && (
            <div className="flex flex-col justify-between gap-2 sm:flex-row">
              <div className="w-full">
                <p className="">
                  Code finess ou numéro Adeli
                  <span className="text-red-500">*</span>
                </p>
                <Controller
                  control={control}
                  name="finessOther"
                  rules={{
                    required: true,
                  }}
                  render={({ field }) => (
                    <Input
                      placeholder="Code finess ou numéro Adeli"
                      size="sm"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={
                        !field.value ||
                        (typeof field.value === "string" &&
                          field.value.trim() === "")
                          ? {
                              message:
                                "Veuillez saisir un code finess ou un numéro Adeli.",
                            }
                          : null
                      }
                    />
                  )}
                />
              </div>

              <div className="w-full">
                <p className="">
                  Raison sociale de l'établissement
                  <span className="text-red-500">*</span>
                </p>
                <Controller
                  control={control}
                  name="socialReason"
                  rules={{
                    required: true,
                  }}
                  render={({ field }) => (
                    <Input
                      placeholder="Raison sociale de l'établissement"
                      size="sm"
                      value={field.value}
                      onChange={field.onChange}
                      error={
                        !field.value ||
                        (typeof field.value === "string" &&
                          field.value.trim() === "")
                          ? {
                              message:
                                "Veuillez saisir la raison sociale de l'établissement.",
                            }
                          : null
                      }
                    />
                  )}
                />
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-col border-b border-b-gray-300">
          {settings.items.careSet.display && (
            <EquipmentSet
              className="border-b border-b-gray-300"
              name="careSet"
              label="set de soins"
              required
              control={control}
              checked={careSetChecked}
              setChecked={setCareSetChecked}
              setValue={setValue}
              defaultValue={settings.items.careSet.default}
            />
          )}
          {settings.items.tegaderm6X7.display && (
            <EquipmentSet
              className="border-b border-b-gray-300"
              name="tegaderm6X7"
              label="films adhésifs"
              required
              control={control}
              checked={tegaderm6X7Checked}
              setChecked={setTegaderm6X7Checked}
              setValue={setValue}
              defaultValue={settings.items.tegaderm6X7.default}
            />
          )}
          {settings.items.tegaderm10X15.display && (
            <EquipmentSet
              className="border-b border-b-gray-300"
              name="tegaderm10X15"
              label="pansements adhésifs films"
              required
              control={control}
              checked={tegaderm10X15Checked}
              setChecked={settegaderm10X15Checked}
              setValue={setValue}
              defaultValue={settings.items.tegaderm10X15.default}
            />
          )}
          <div className="flex flex-col gap-2 py-3">
            {settings.items.autocontrol.display && (
              <Controller
                control={control}
                name="autocontrol"
                render={({ field }) => (
                  <div className="flex items-center">
                    <Checkbox
                      name="autocontrol"
                      checked={field.value}
                      onChange={(checked) => field.onChange(checked)}
                    />
                    <p
                      className="cursor-pointer hover:underline"
                      onClick={() =>
                        setValue("autocontrol", !field.value, {
                          shouldFocus: true,
                        })
                      }
                    >
                      {autoControlLabel || ""}
                    </p>
                  </div>
                )}
              />
            )}
            {settings.items.autocontrol2.display && (
              <Controller
                control={control}
                name="autocontrol2"
                render={({ field }) => (
                  <div className="flex items-center">
                    <Checkbox
                      name="autocontrol2"
                      checked={field.value}
                      onChange={(checked) => field.onChange(checked)}
                    />
                    <p
                      className="cursor-pointer hover:underline"
                      onClick={() =>
                        setValue("autocontrol2", !field.value, {
                          shouldFocus: true,
                        })
                      }
                    >
                      {autoControl2Label || ""}
                    </p>
                  </div>
                )}
              />
            )}
          </div>
        </div>
      </div>
      {missingDataChecked && (
        <div className="text-xs text-red-500">
          Veuillez remplir tous les champs obligatoires.
        </div>
      )}
      <div className="flex gap-2 justify-end items-center">
        {isSuperAdmin && (
          <button
            type="submit"
            className="px-3 py-1 font-semibold border rounded w-fit disabled:text-dark border-dark text-dark hover:bg-dark disabled:bg-transparent hover:text-white"
            disabled={isSubmitting || Object.keys(errors).length > 0}
            onClick={() => {
              if (isSuperAdmin) setAdminMode("print");
            }}
          >
            {isSubmitting ? <Loader /> : "Télécharger"}
          </button>
        )}
        <button
          type="submit"
          className="px-3 py-1 font-semibold border rounded w-fit disabled:text-dark border-dark text-dark hover:bg-dark disabled:bg-transparent hover:text-white"
          disabled={isSubmitting || Object.keys(errors).length > 0}
          onClick={() => {
            if (isSuperAdmin) setAdminMode("sign");
          }}
        >
          {isSubmitting ? (
            <Loader />
          ) : printMode ? (
            "Télécharger"
          ) : (
            "Signer le renouvellement"
          )}
        </button>
      </div>
    </form>
  );
};

export default RenewalForm;
